import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateTagInput,
  ReplaceTagsInput,
  Tag,
  TagRef,
  UpdateTagInput,
} from '@neuroedge/shared';
import { apiClient, ApiClientError } from './client';

const KEY = ['tags'] as const;

export const listTags = () => apiClient.get<Tag[]>('/tags');
export const createTag = (input: CreateTagInput) => apiClient.post<Tag>('/tags', input);
export const updateTag = (id: string, patch: UpdateTagInput) =>
  apiClient.patch<Tag>(`/tags/${id}`, patch);
export const deleteTag = (id: string) => apiClient.delete<void>(`/tags/${id}`);

export const setContactTags = (contactId: string, input: ReplaceTagsInput) =>
  apiClient.put<TagRef[]>(`/contacts/${contactId}/tags`, input);
export const setLeadTags = (leadId: string, input: ReplaceTagsInput) =>
  apiClient.put<TagRef[]>(`/leads/${leadId}/tags`, input);

export function useTags() {
  return useQuery<Tag[], ApiClientError>({ queryKey: [...KEY, 'list'], queryFn: listTags });
}

export function useCreateTag() {
  const qc = useQueryClient();
  return useMutation<Tag, ApiClientError, CreateTagInput>({
    mutationFn: createTag,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateTag() {
  const qc = useQueryClient();
  return useMutation<Tag, ApiClientError, { id: string; patch: UpdateTagInput }>({
    mutationFn: ({ id, patch }) => updateTag(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteTag() {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: deleteTag,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: ['contacts'] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useSetContactTags(contactId: string) {
  const qc = useQueryClient();
  return useMutation<TagRef[], ApiClientError, ReplaceTagsInput>({
    mutationFn: (input) => setContactTags(contactId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

export function useSetLeadTags(leadId: string) {
  const qc = useQueryClient();
  return useMutation<TagRef[], ApiClientError, ReplaceTagsInput>({
    mutationFn: (input) => setLeadTags(leadId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}
